import { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { useCarouselCards } from '../../../assets/hooks/useCardData';
import {
  isActivatingCardStatus,
  isCardIssueFundingCovered,
  isCardLimitReached,
  isCardFrozen,
  isNotIssuedCardStatus,
} from '../../../assets/services/cards';
import { setDepositCard, setDepositReturnPath } from '../../../assets/services/deposit';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './ChooseCardSheet.css';

function ChooseCardSheet({ onClose, onSelectCard, selectedCardId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { card, deposit, home } = useContext(TranslationContext);
  const { currentUser } = useContext(CurrentUserContext);
  const allCards = useCarouselCards();
  const cards = allCards.filter((card) => (
    card.status === 'active' && !isCardFrozen(card.id)
  ));
  const notIssuedCard = allCards.find((cardItem) => isNotIssuedCardStatus(cardItem.status));
  const isNotIssuedCardFundingCovered =
    Boolean(notIssuedCard && isCardIssueFundingCovered(notIssuedCard));
  const hasIssueInProgress = allCards.some((cardItem) => (
    isNotIssuedCardStatus(cardItem.status) || isActivatingCardStatus(cardItem.status)
  ));
  const hasReachedCardLimit = isCardLimitReached(allCards, currentUser);
  const shouldShowIssueContinueState =
    cards.length === 0 && Boolean(notIssuedCard) && !isNotIssuedCardFundingCovered;
  const shouldShowIssueWaitState =
    cards.length === 0
    && hasIssueInProgress
    && (!notIssuedCard || isNotIssuedCardFundingCovered);
  const autoSelectCardId =
    cards.length === 1 && !shouldShowIssueContinueState && !shouldShowIssueWaitState
      ? cards[0].id
      : '';
  const isClosingRef = useRef(false);

  const performSelect = useCallback((cardId) => {
    if (onSelectCard) {
      onSelectCard(cardId);
      return;
    }

    const returnPath = `${location.pathname}${location.search}${location.hash}`;

    setDepositCard(cardId);
    setDepositReturnPath(returnPath);
    navigate('/deposit', {
      state: { returnPath },
    });
  }, [location.hash, location.pathname, location.search, navigate, onSelectCard]);

  const closeThen = (callback) => {
    if (isClosingRef.current) return;

    isClosingRef.current = true;
    onClose();
    callback();
  };

  const handleSelect = (cardId) => {
    closeThen(() => {
      performSelect(cardId);
    });
  };

  useEffect(() => {
    if (!autoSelectCardId || isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    onClose();
    performSelect(autoSelectCardId);
  }, [autoSelectCardId, onClose, performSelect]);

  const handleContinueIssue = () => {
    if (!notIssuedCard?.id) return;

    closeThen(() => {
      navigate(`/cards/new?continueIssue=1&hideBack=1&cardId=${encodeURIComponent(notIssuedCard.id)}`, {
        state: {
          fromPendingCard: true,
          hideBack: true,
          cardId: notIssuedCard.id,
        },
      });
    });
  };

  if (autoSelectCardId) {
    return null;
  }

  return (
    <BottomSheet
      className="choose-card-sheet"
      title={deposit.topUpCard}
      titleId="choose-card-sheet-title"
      onClose={onClose}
    >
      <p className="choose-card-sheet__subtitle">{card.chooseCardForTopUp}</p>
      <div className="choose-card-sheet__list">
        {shouldShowIssueContinueState ? (
          <div className="choose-card-sheet__empty" role="status">
            <span className="choose-card-sheet__empty-icon" aria-hidden="true">
              +
            </span>
            <span className="choose-card-sheet__empty-main">
              <span className="choose-card-sheet__empty-title">
                {card.topUpToActivate}
              </span>
              <span className="choose-card-sheet__empty-text">
                {deposit.notIssuedTopUpText}
              </span>
            </span>
            <button
              type="button"
              className="choose-card-sheet__empty-action"
              onClick={handleContinueIssue}
            >
              {card.topUpToActivate}
            </button>
          </div>
        ) : null}

        {shouldShowIssueWaitState ? (
          <div
            className={
              isNotIssuedCardFundingCovered
                ? 'choose-card-sheet__empty choose-card-sheet__empty--no-icon'
                : 'choose-card-sheet__empty'
            }
            role="status"
          >
            {!isNotIssuedCardFundingCovered && (
              <span className="choose-card-sheet__empty-icon" aria-hidden="true">
                •
              </span>
            )}
            <span className="choose-card-sheet__empty-main">
              <span className="choose-card-sheet__empty-title">
                {isNotIssuedCardFundingCovered
                  ? card.issuePaymentReceivedTitle
                  : deposit.noTopUpCardsTitle}
              </span>
              <span className="choose-card-sheet__empty-text">
                {isNotIssuedCardFundingCovered
                  ? card.issuePaymentReceivedText
                  : deposit.noTopUpCardsText}
              </span>
            </span>
          </div>
        ) : null}

        {cards.map((card) => (
          <button
            key={card.id}
            type="button"
            className={
              card.id === selectedCardId
                ? 'choose-card-sheet__item choose-card-sheet__item--selected'
                : 'choose-card-sheet__item'
            }
            onClick={() => handleSelect(card.id)}
          >
            <span
              className={`choose-card-sheet__swatch choose-card-sheet__swatch--${card.style}`}
              aria-hidden="true"
            />
            <span className="choose-card-sheet__item-main">
              <span className="choose-card-sheet__item-label">{card.label}</span>
              <span className="choose-card-sheet__item-last-four">•••• {card.lastFour}</span>
            </span>
            <span className="choose-card-sheet__item-balance">{card.balance}</span>
          </button>
        ))}

        {!hasIssueInProgress && !hasReachedCardLimit ? (
          <button
            type="button"
            className="choose-card-sheet__add"
            onClick={() => {
              closeThen(() => navigate('/cards/new'));
            }}
          >
            <span className="choose-card-sheet__add-icon" aria-hidden="true">
              +
            </span>
            <span>{home.addNewCard}</span>
          </button>
        ) : null}
      </div>
    </BottomSheet>
  );
}

export default ChooseCardSheet;
